import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Download, Eye, Printer, ReceiptIndianRupee, Search } from "lucide-react";
import { toast } from "sonner";
import { DashboardShell, PlaceholderCard } from "@/components/DashboardShell";
import { PARTNER_NAV } from "@/lib/nav";
import { useAuth } from "@/lib/auth";
import {
  BILLING_INVOICE_STATUSES,
  BILLING_INVOICE_TYPES,
  billingStatusMeta,
  billingTypeLabel,
  fetchBillingInvoices,
  formatAmount,
  formatDate,
  type BillingInvoiceStatus,
  type BillingInvoiceType,
} from "@/lib/partner-billing";

export const Route = createFileRoute("/_authenticated/partner/billing/")({
  head: () => ({
    meta: [
      { title: "Invoices · Landoo Partner" },
      {
        name: "description",
        content:
          "View your Landoo partner invoices for subscriptions, contact unlocks and future services with paid, pending and cancelled status.",
      },
      { property: "og:title", content: "Invoices · Landoo Partner" },
      {
        property: "og:description",
        content: "Track subscription, contact unlock and future service invoices in one place.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: BillingInvoicesPage,
});

function BillingInvoicesPage() {
  const { user } = useAuth();
  const [status, setStatus] = useState<BillingInvoiceStatus | "all">("all");
  const [type, setType] = useState<BillingInvoiceType | "all">("all");
  const [q, setQ] = useState("");

  const { data = [], isLoading } = useQuery({
    queryKey: ["partner-billing-invoices", user?.id],
    queryFn: () => fetchBillingInvoices(user!.id),
    enabled: !!user,
  });

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return data.filter(
      (inv) =>
        (status === "all" || inv.status === status) &&
        (type === "all" || inv.invoice_type === type) &&
        (!term ||
          inv.invoice_number.toLowerCase().includes(term) ||
          (inv.description ?? "").toLowerCase().includes(term) ||
          (inv.service_type ?? "").toLowerCase().includes(term)),
    );
  }, [data, status, type, q]);

  const counts = useMemo(() => {
    const base: Record<string, number> = { all: data.length };
    for (const s of BILLING_INVOICE_STATUSES)
      base[s.value] = data.filter((i) => i.status === s.value).length;
    return base;
  }, [data]);

  return (
    <DashboardShell title="Invoices" role="Partner" items={PARTNER_NAV}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <ReceiptIndianRupee className="h-4 w-4" />
          {isLoading ? "Loading…" : `${rows.length} of ${data.length} invoice(s)`}
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search invoice number…"
            className="w-full rounded-full border border-border bg-card py-2 pl-9 pr-3 text-sm outline-none focus:border-coral"
          />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {[{ value: "all", label: "All" }, ...BILLING_INVOICE_STATUSES].map((s) => (
          <button
            key={s.value}
            onClick={() => setStatus(s.value as BillingInvoiceStatus | "all")}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
              status === s.value ? "bg-coral text-white" : "bg-secondary text-muted-foreground"
            }`}
          >
            {s.label} ({counts[s.value] ?? 0})
          </button>
        ))}
      </div>

      <div className="mt-2 flex flex-wrap gap-2">
        {[{ value: "all", label: "All types" }, ...BILLING_INVOICE_TYPES].map((t) => (
          <button
            key={t.value}
            onClick={() => setType(t.value as BillingInvoiceType | "all")}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
              type === t.value ? "border-coral text-coral" : "border-border text-muted-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {rows.length > 0 ? (
        <>
          {/* Desktop table */}
          <div className="mt-4 hidden overflow-x-auto rounded-2xl border border-border bg-card shadow-soft md:block">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="bg-secondary/50 text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Invoice number</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Invoice date</th>
                  <th className="px-3 py-2">Amount</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((inv) => {
                  const st = billingStatusMeta(inv.status);
                  return (
                    <tr key={inv.id} className="border-t border-border">
                      <td className="px-3 py-2 font-semibold text-brown-dark">
                        <Link
                          to="/partner/billing/$invoiceId"
                          params={{ invoiceId: inv.id }}
                          className="hover:underline"
                          search={{ print: undefined }}
                        >
                          {inv.invoice_number}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {billingTypeLabel(inv.invoice_type)}
                      </td>
                      <td className="px-3 py-2 text-xs">{formatDate(inv.invoice_date)}</td>
                      <td className="px-3 py-2 font-semibold text-coral">
                        {formatAmount(inv.total || inv.amount)}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`rounded-full px-2 py-1 text-[10px] font-semibold ${st.cls}`}
                        >
                          {st.label}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <RowActions id={inv.id} number={inv.invoice_number} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="mt-4 grid gap-3 md:hidden">
            {rows.map((inv) => {
              const st = billingStatusMeta(inv.status);
              return (
                <div
                  key={inv.id}
                  className="rounded-2xl border border-border bg-card p-4 shadow-soft"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <Link
                        to="/partner/billing/$invoiceId"
                        params={{ invoiceId: inv.id }}
                        className="font-semibold text-brown-dark hover:underline"
                        search={{ print: undefined }}
                      >
                        {inv.invoice_number}
                      </Link>
                      <p className="text-xs text-muted-foreground">
                        {billingTypeLabel(inv.invoice_type)}
                      </p>
                    </div>
                    <span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${st.cls}`}>
                      {st.label}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-sm">
                    <span className="text-xs text-muted-foreground">
                      {formatDate(inv.invoice_date)}
                    </span>
                    <span className="font-semibold text-coral">
                      {formatAmount(inv.total || inv.amount)}
                    </span>
                  </div>
                  <div className="mt-3">
                    <RowActions id={inv.id} number={inv.invoice_number} />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="mt-4">
          <PlaceholderCard
            title={isLoading ? "Loading invoices…" : "No invoices yet"}
            description="Invoices for subscriptions, contact unlocks and future services will appear here once they are raised for your account."
          />
        </div>
      )}
    </DashboardShell>
  );
}

function RowActions({ id, number }: { id: string; number: string }) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-1.5">
      <Link
        to="/partner/billing/$invoiceId"
        params={{ invoiceId: id }}
        className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1.5 text-[11px] font-semibold text-brown-dark"
        search={{ print: undefined }}
      >
        <Eye className="h-3.5 w-3.5" /> View
      </Link>
      <button
        onClick={() => toast.info(`PDF download for ${number} will be available soon.`)}
        className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1.5 text-[11px] font-semibold text-muted-foreground"
      >
        <Download className="h-3.5 w-3.5" /> PDF
      </button>
      <Link
        to="/partner/billing/$invoiceId"
        params={{ invoiceId: id }}
        search={{ print: true }}
        className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1.5 text-[11px] font-semibold text-muted-foreground"
      >
        <Printer className="h-3.5 w-3.5" /> Print
      </Link>
    </div>
  );
}
